import { useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { FileText, Trash2, AlertCircle } from 'lucide-react'
import type { AttendanceSnapshot } from '@/types/attendance-metrics'

interface SnapshotListProps {
  snapshots: AttendanceSnapshot[]
  selectedIds?: string[]
  onSelectionChange?: (ids: string[]) => void
  onCompare?: () => void
  onDelete?: (id: string) => void
  onSnapshotClick?: (id: string) => void
  isLoading?: boolean
}

function formatTime(seconds: number | null): string {
  if (seconds === null) return 'N/A'
  if (seconds < 60) return `${seconds.toFixed(0)}s`
  return `${(seconds / 60).toFixed(1)}min`
}

export function SnapshotList({
  snapshots,
  selectedIds = [],
  onSelectionChange,
  onCompare,
  onDelete,
  onSnapshotClick,
  isLoading,
}: SnapshotListProps) {
  const [internalSelected, setInternalSelected] = useState<string[]>(selectedIds)

  const selected = onSelectionChange ? selectedIds : internalSelected
  const setSelected = onSelectionChange || setInternalSelected

  const toggleSelection = (id: string) => {
    const newSelected = selected.includes(id)
      ? selected.filter((sid) => sid !== id)
      : selected.length < 2
        ? [...selected, id]
        : [selected[1], id] // Replace first if at limit

    setSelected(newSelected)
  }

  const canCompare = selected.length === 2

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    )
  }

  if (!snapshots.length) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center space-y-2">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto" />
            <h3 className="font-semibold text-lg">Nenhum relatório salvo</h3>
            <p className="text-sm text-muted-foreground">
              Crie um relatório consolidado para comparação futura
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-3">
      {/* Mobile: Cards */}
      <div className="md:hidden space-y-3">
        {snapshots.map((snapshot) => {
          const isSelected = selected.includes(snapshot.id)
          const displayName = snapshot.name || new Date(snapshot.createdAt).toLocaleString('pt-BR')

          return (
            <Card
              key={snapshot.id}
              className={`cursor-pointer transition-all ${
                isSelected ? 'border-primary bg-primary/5' : ''
              }`}
              onClick={(e) => {
                if (e.target instanceof HTMLInputElement) return
                onSnapshotClick ? onSnapshotClick(snapshot.id) : toggleSelection(snapshot.id)
              }}
            >
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <Checkbox checked={isSelected} onCheckedChange={() => toggleSelection(snapshot.id)} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="h-4 w-4 text-primary" />
                      <h3 className="font-semibold truncate">{displayName}</h3>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {new Date(snapshot.startDate).toLocaleDateString('pt-BR')} até{' '}
                      {new Date(snapshot.endDate).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                  {onDelete && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 shrink-0"
                      onClick={(e) => {
                        e.stopPropagation()
                        onDelete(snapshot.id)
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-muted-foreground text-xs">FRT</p>
                    <p className="font-semibold">{formatTime(snapshot.frtAvgSeconds)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">TMR</p>
                    <p className="font-semibold">{formatTime(snapshot.tmrAvgSeconds)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Tickets</p>
                    <Badge variant="outline">{snapshot.totalTickets}</Badge>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Mensagens</p>
                    <Badge variant="outline">{snapshot.totalMessages}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Desktop: Table */}
      <div className="hidden md:block border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]"></TableHead>
              <TableHead>Nome</TableHead>
              <TableHead>Período</TableHead>
              <TableHead>FRT</TableHead>
              <TableHead>TMR</TableHead>
              <TableHead>Tickets</TableHead>
              <TableHead>Mensagens</TableHead>
              <TableHead className="w-[100px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {snapshots.map((snapshot) => {
              const isSelected = selected.includes(snapshot.id)
              const displayName = snapshot.name || new Date(snapshot.createdAt).toLocaleString('pt-BR')

              return (
                <TableRow
                  key={snapshot.id}
                  className={`cursor-pointer ${isSelected ? 'bg-primary/5' : ''}`}
                  onClick={(e) => {
                    if (e.target instanceof HTMLInputElement) return
                    onSnapshotClick ? onSnapshotClick(snapshot.id) : toggleSelection(snapshot.id)
                  }}
                >
                  <TableCell>
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleSelection(snapshot.id)}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{displayName}</TableCell>
                  <TableCell className="text-sm">
                    {new Date(snapshot.startDate).toLocaleDateString('pt-BR')} até{' '}
                    {new Date(snapshot.endDate).toLocaleDateString('pt-BR')}
                  </TableCell>
                  <TableCell>{formatTime(snapshot.frtAvgSeconds)}</TableCell>
                  <TableCell>{formatTime(snapshot.tmrAvgSeconds)}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{snapshot.totalTickets}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{snapshot.totalMessages}</Badge>
                  </TableCell>
                  <TableCell>
                    {onDelete && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation()
                          onDelete(snapshot.id)
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>

      {/* Compare Button */}
      {onCompare && (
        <div className="flex justify-center pt-2">
          <Button onClick={onCompare} disabled={!canCompare}>
            Comparar Selecionados {selected.length > 0 && `(${selected.length}/2)`}
          </Button>
        </div>
      )}
    </div>
  )
}
